/**
 * The law: heat and wanted stars, cruisers and helicopters that chase, officers who get out,
 * and arrests. Split out of game.ts; everything here is a function over the Game.
 */
import { smokeBetween } from './charges';
import { count as countAch } from './achievements';
import { report } from './newsfeed';
import { RF_H, RF_SIDEWALK, RF_V } from '../world/generator';
import { forward } from './entities';
import { chaseAir, chaseNpc, driveNpc, lineClear, roadCentre } from './ai';
import { makeVehicle, type Person } from './entities';
import type { Game, PlayerState } from './game';
import { vehicleBlocked } from './physics';
import { doorstep } from './property';
import { fail } from './jobs';
import { spawnOfficers } from './threats';

/** An officer got hold of you: a fine, the stars are gone, you wake up outside the nearest police station (or hospital). */
/** How hard the law comes down: heat per crime, cruiser bullet damage, the star from which cruisers shoot, and cruisers per star. */
export interface PoliceLevel { heat: number; dmg: number; shootFrom: number; carsPerStar: number; /** roadblocks: from this many stars (0 = never), and seconds between them (server `--roadblocks STARS,SECONDS`) */ roadblockFrom: number; roadblockEvery: number }
export const POLICE_LEVELS: Record<'easy' | 'normal' | 'hard', PoliceLevel> = {
  easy: { heat: 0.6, dmg: 0.6, shootFrom: 4, carsPerStar: 0.6, roadblockFrom: 5, roadblockEvery: 40 },
  normal: { heat: 1, dmg: 1, shootFrom: 3, carsPerStar: 1, roadblockFrom: 4, roadblockEvery: 25 },
  hard: { heat: 1.4, dmg: 1.5, shootFrom: 2, carsPerStar: 1.4, roadblockFrom: 3, roadblockEvery: 18 },
};

export function bust(game: Game, st: PlayerState, by: Person): void {
  const p = st.person;
  if (p.dead || st.bustedAt !== undefined && game.time - st.bustedAt < 5) return;
  st.bustedAt = game.time;
  by.act = 'cuffing'; by.actUntil = game.time + 2.5; // (owner, Sep 28) the officer's hands at the cuffs
  countAch(game, st, 'busted', 1);
  // the take of this wanted spell goes back first (owner: no profit from a robbery you were caught for), then the fine on what is left
  const back = Math.min(p.items.cash ?? 0, st.hot); p.items.cash = (p.items.cash ?? 0) - back; st.hot = 0;
  const cash = p.items.cash ?? 0;
  const eco = game.reg.economy.law;
  const fine = Math.min(Math.floor(cash * eco.fineShare), eco.fineCapPerStar * Math.max(1, st.wanted));
  p.items.cash = cash - fine;
  if (back > 0) game.say(`The $${back} you took is gone`, p);
  st.heat = 0; st.wanted = 0; st.heatHold = 0;
  for (const v of game.vehicles) if (v.ai?.chase && v.ai.target === p.id) { v.ai = undefined; v.home = 'lost'; }
  for (const q of game.people) if (q.police?.target === p.id && q.role === 'police') { if (q.police.beat) { q.police = undefined; q.walker = { dx: 0, dy: 0, think: 0, idle: 1 }; } else q.police.t = 999; }
  if (st.rob) st.rob = undefined;
  if (st.job) fail(game, st, 'Busted');
  // the station: nearest police-tagged building in the loaded world, else the hospital fallback of respawn()
  let best: { x: number; y: number } | undefined, bd = Infinity;
  for (const lc of game.world.liveChunks()) for (const b of lc.chunk.buildings) {
    if (!b.def.tags.includes('police') || b.height <= 0) continue;
    const d = Math.hypot(b.x - p.x, b.y - p.y); if (d < bd) { bd = d; best = doorstep(b, game.seed); }
  }
  p.inside = undefined; p.floor = 0; p.z = 0;
  if (best) { p.x = best.x; p.y = best.y; } else game.respawn(p);
  p.hp = Math.max(p.hp, p.maxHp * 0.5);
  game.say(`Busted by ${by.role === 'police' ? 'the police' : 'an officer'} — $${fine} fine`, p);
  game.events.push({ kind: 'click', x: p.x, y: p.y });
}

/** Player did something the law dislikes. */
export function crime(game: Game, amount: number, who: Person, floor = 0): void {
  const st = game.stateOf(who);
  if (!st) return;
  // a beat cop who sees it drops the stroll and comes after you
  for (const q of game.people) if (q.role === 'police' && q.walker && !q.dead && Math.hypot(q.x - who.x, q.y - who.y) < 28) { q.walker = undefined; q.police = { target: who.id, cool: 0.5, t: 0, beat: true }; game.say('A cop saw that', who); }
  st.heat = Math.max(Math.min(6, st.heat + amount * game.police.heat), Math.min(6, floor)); // (`floor`: a deed that is this many stars whatever the difficulty — a stolen cruiser)
  st.heatHold = 20;
  const lvl = Math.min(5, Math.floor(st.heat));
  if (lvl > st.wanted) { st.wanted = lvl; game.say(`Wanted level ${'★'.repeat(lvl)}`, who); if (lvl === 1) st.policeTimer = 2; if (lvl >= 4) report(game, 'wanted', who.x, who.y, { what: who.vehicle ? `a ${who.vehicle.def.name.toLowerCase()}` : 'on foot', n: String(lvl) }, st.name); }
}

/** How far a police unit sees (tiles): a cruiser, an officer on foot, a helicopter — drawn as a faint ring while you are wanted (owner's ask). */
export const SIGHT = { car: 28, foot: 18, air: 45 } as const;
export function sightOf(v: { def: { class: string } }): number { return v.def.class === 'helicopter' ? SIGHT.air : SIGHT.car; }
/** Seconds of hiding it takes to lose one star at this level: the more stars, the longer (1: 10 s … 5: 40 s). */
export function hideSecondsPerStar(wanted: number, karma = 0, spread = 0): number { return [10, 10, 15, 22, 30, 40][Math.max(1, Math.min(5, wanted))] * (1 - spread * Math.max(-100, Math.min(100, karma)) / 100); } // (a good name: they lose interest sooner)
/** Is any unit looking at the player right now? Cruisers and the helicopter see outdoors within their ring; indoors only an officer in the same building does. */
export function seenByPolice(game: Game, p: Person): boolean {
  if (p.dive) return false; // under the surface: out of sight (docs/DESIGN_DIVING.md)
  if (p.inside) return game.people.some((q) => q.role === 'police' && !q.dead && q.inside === p.inside && Math.hypot(q.x - p.x, q.y - p.y) < SIGHT.foot);
  for (const v of game.vehicles) { if (v.wrecked || !(v.ai?.chase || v.home === 'police' || v.def.tags.includes('police'))) continue; if (Math.hypot(v.x - p.x, v.y - p.y) < sightOf(v) && !smokeBetween(game, v.x, v.y, p.x, p.y)) return true; } // (Sep 30: not through smoke)
  return game.people.some((q) => q.role === 'police' && !q.dead && !q.inside && !((q.stunUntil ?? 0) > game.time) && Math.hypot(q.x - p.x, q.y - p.y) < SIGHT.foot && !smokeBetween(game, q.x, q.y, p.x, p.y));
}

export function updatePolice(game: Game, dt: number): void {
  for (const st of game.players.values()) updatePoliceFor(game, st, dt);
  // chasers whose target left keep nothing to do
  for (const v of game.vehicles) if (v.ai?.chase && v.ai.target !== undefined && !game.players.has(v.ai.target)) { v.ai = undefined; v.home = 'lost'; }
}

function updatePoliceFor(game: Game, st: PlayerState, dt: number): void {
  const p = st.person;
  const chasers = game.vehicles.filter((v) => v.ai?.chase && v.ai.target === p.id && !v.wrecked);
  // in sight of a unit the heat holds; out of sight it drains, slower the more stars you carry (10 s a star at one, 40 s at five)
  const seen = st.wanted > 0 && seenByPolice(game, p);
  if (seen) st.heatHold = Math.max(st.heatHold, 3);
  if (st.heatHold > 0) st.heatHold -= dt;
  else if (!(p.inside && p.floor < 0)) st.heat = Math.max(0, st.heat - dt / hideSecondsPerStar(st.wanted || 1, st.stats.karma, game.reg.economy.karma.heatSpread)); // (§24: on the garage floor the heat freezes — a moment to patch up and switch cars, never a chase ended by a door)
  const lvl = Math.min(5, Math.floor(st.heat));
  if (lvl < st.wanted) { st.wanted = lvl; if (lvl === 0) { game.say(st.hot > 0 ? `Lost them — the $${st.hot} is yours to keep` : 'Lost them', p); st.hot = 0; for (const v of chasers) { v.ai = undefined; v.home = 'lost'; } for (const v of game.vehicles) if (v.home === 'police' && !v.ai && v.siren) { v.siren = false; v.home = 'lost'; } } } // (roadblocks pack up too)
  if (st.wanted === 0) return;
  st.policeTimer -= dt;
  const want = Math.min(5, Math.max(1, Math.round(st.wanted * game.police.carsPerStar))); // one cruiser per star (normal)
  if (st.policeTimer <= 0 && chasers.length < want) {
    st.policeTimer = Math.max(6, 18 - st.wanted * 2);
    spawnPoliceCar(game, p);
  }
  // enough stars and moving fast: a roadblock goes up on the road ahead every so often — cruisers across the lanes, officers beside them
  const rbFrom = game.police.roadblockFrom, rbEvery = game.police.roadblockEvery;
  if (rbFrom > 0 && st.wanted >= rbFrom && p.vehicle && p.vehicle.driver === p && Math.abs(p.vehicle.speed) > 8 && p.vehicle.alt < 1 && game.time - (st.roadblockAt ?? -1e9) > rbEvery) {
    if (spawnRoadblock(game, st)) st.roadblockAt = game.time;
  }
  const heliCount = chasers.filter((v) => v.def.class === 'helicopter').length;
  if (st.wanted >= 3 && heliCount === 0 && st.policeTimer < 6) spawnPoliceHeli(game, p);
  // a raid: at three stars a patrol car that has pulled up near the building you are in sends two officers in on foot
  if (st.wanted >= 3 && p.inside && !p.dead) {
    const officers = game.people.filter((q) => q.police?.target === p.id && !q.dead).length;
    for (const v of chasers) {
      if (v.def.class === 'helicopter' || v.ai!.unloaded || officers >= 4) continue;
      if (Math.hypot(v.x - p.x, v.y - p.y) < 16 && Math.abs(v.speed) < 3) { v.ai!.unloaded = true; spawnOfficers(game, v.x, v.y, p.id); game.say('Police are coming in!', p); }
    }
  }
  // on foot outside: a cruiser that has caught up with you drops an officer to run you down (an arrest at one star, a gunfight above)
  if (!p.inside && !p.vehicle && !p.dead) {
    const officers = game.people.filter((q) => q.police?.target === p.id && !q.dead).length;
    for (const v of chasers) {
      if (v.def.class === 'helicopter' || v.ai!.unloaded || officers >= 2) continue;
      if (Math.hypot(v.x - p.x, v.y - p.y) < 10 && Math.abs(v.speed) < 4) { v.ai!.unloaded = true; spawnOfficers(game, v.x, v.y, p.id, 1); game.say(st.wanted <= 1 ? 'An officer is after you — run!' : 'An officer got out', p); }
    }
  }
  for (const v of chasers) {
    if (v.driver && game.isPlayerId(v.driver.id)) continue;
    if (v.def.class === 'helicopter') {
      if (chaseAir(game.world, v, p, dt) && !p.dead) {
        const gun = game.reg.get('weapon', 'smg_compact') ?? game.reg.get('weapon', 'pistol_9mm');
        if (gun) { const a = Math.atan2(p.x - v.x, -(p.y - v.y)) + (game.npcRng.float() - 0.5) * 0.3; game.fireWeapon({ ...gun, damage: 4 * game.police.dmg, range: 40 }, v.x, v.y, a, 1.5, -2, `v:${v.id}`); }
      }
      continue;
    }
    const d = Math.hypot(v.x - p.x, v.y - p.y);
    const direct = d < 18 && lineClear(game.world, v.x, v.y, p.x, p.y);
    let fired = false;
    if (direct) fired = chaseNpc(game.world, v, p, dt, !p.vehicle);
    else {
      v.ai!.ignorePeds = 5;
      driveNpc(game.world, v, game.vehicles, game.people, game.npcRng, dt, p);
      v.ai!.shootCooldown = Math.max(0, (v.ai!.shootCooldown ?? 0) - dt);
      if (d < 22 && v.ai!.shootCooldown === 0 && lineClear(game.world, v.x, v.y, p.x, p.y)) { v.ai!.shootCooldown = 0.9; fired = true; }
    }
    if (fired && st.wanted >= game.police.shootFrom) { // one star: they follow and drop an officer; two: they ram; from three (normal) they shoot
      const gun = game.reg.get('weapon', 'pistol_9mm');
      if (gun && !p.dead) {
        const a = Math.atan2(p.x - v.x, -(p.y - v.y)) + (game.npcRng.float() - 0.5) * 0.25;
        game.fireWeapon({ ...gun, damage: 5 * game.police.dmg }, v.x, v.y, a, 1.2, -2, `v:${v.id}`);
      }
    }
  }
}

function spawnPoliceHeli(game: Game, p: Person): void {
  const defs = game.reg.all('vehicle').filter((v) => v.tags.includes('police') && v.class === 'helicopter');
  if (!defs.length) return;
  const a = game.npcRng.float() * Math.PI * 2;
  const v = makeVehicle(game.npcRng.pick(defs), p.x + Math.cos(a) * 90, p.y + Math.sin(a) * 90, 0, 'police');
  v.alt = 30; v.speed = 15;
  v.ai = { axis: 'v', dir: 1, wait: 0, lost: 0, blockedFor: 0, overtake: 0, stuck: 0, reverse: 0, ignorePeds: 0, redFor: 0, chase: true, target: p.id, shootCooldown: 2 };
  game.vehicles.push(v);
  game.say('Police helicopter inbound', p);
}

function spawnPoliceCar(game: Game, p: Person): void {
  const defs = game.reg.all('vehicle').filter((v) => v.tags.includes('police') && v.class !== 'helicopter' && v.class !== 'plane' && v.class !== 'boat');
  if (!defs.length) return;
  const def = game.npcRng.pick(defs);
  for (let tries = 0; tries < 40; tries++) {
    const a = game.npcRng.float() * Math.PI * 2, r = 45 + game.npcRng.float() * 25;
    const x = p.x + Math.cos(a) * r, y = p.y + Math.sin(a) * r;
    if (!game.world.roadAt(x, y) || game.world.roadFlags(x, y) & RF_SIDEWALK) continue;
    const hit = game.world.gen.net.hitsAt(Math.floor(x), Math.floor(y)).find((h) => h.road.class !== 'rail');
    if (!hit) continue;
    const axis = hit.axis;
    const dir: 1 | -1 = (axis === 'v' ? p.y - y : p.x - x) >= 0 ? 1 : -1;
    const angle = axis === 'v' ? (dir === -1 ? 0 : Math.PI) : dir === 1 ? Math.PI / 2 : -Math.PI / 2;
    const v = makeVehicle(def, x, y, angle, 'police');
    if (vehicleBlocked(game.world, v, x, y, v.angle, 'drive') || game.vehicles.some((o) => Math.hypot(o.x - x, o.y - y) < 6)) continue;
    v.ai = { axis, dir, wait: 0, lost: 0, blockedFor: 0, overtake: 0, stuck: 0, reverse: 0, ignorePeds: 0, redFor: 0, chase: true, target: p.id, shootCooldown: 1 };
    game.vehicles.push(v);
    game.events.push({ kind: 'click', x, y });
    return;
  }
}


/** A roadblock 90–130 m ahead of a fleeing driver: cruisers parked across the carriageway (light bars on), two officers beside. */
function spawnRoadblock(game: Game, st: PlayerState): boolean {
  const p = st.person, v = p.vehicle!;
  const defs = game.reg.all('vehicle').filter((d) => d.tags.includes('police') && d.class === 'car');
  if (!defs.length) return false;
  const [fx, fy] = forward(v.angle);
  const axis: 'v' | 'h' = Math.abs(fx) > Math.abs(fy) ? 'h' : 'v'; // the road runs along the heading
  const w = game.world;
  for (let dist = 90; dist <= 130; dist += 4) {
    const x = v.x + fx * dist, y = v.y + fy * dist;
    if (!w.loaded(x, y)) return false;
    const f = w.roadFlags(x, y);
    if (!w.roadAt(x, y) || f & RF_SIDEWALK || ((f & RF_V) && (f & RF_H))) continue; // carriageway, not a junction
    if (w.deckH(x, y) > 0) continue;
    const hit = w.gen.net.hitsAt(Math.floor(x), Math.floor(y)).find((h) => h.axis === axis);
    if (!hit) continue;
    const width = hit.road.width, centre = roadCentre(hit); // (the exact stroke centre on the path world, Sep 17)
    const across = axis === 'v' ? Math.PI / 2 : 0; // cars turned 90° to the road
    const n = width >= 6 ? 2 : 1;
    const cars = [];
    for (let i = 0; i < n; i++) {
      const off = n === 1 ? 0 : (i === 0 ? -1 : 1) * Math.min(2.4, width / 4);
      const cx = axis === 'v' ? centre + off : x, cy = axis === 'v' ? y : centre + off;
      const car = makeVehicle(game.npcRng.pick(defs), cx, cy, across, 'police');
      if (vehicleBlocked(w, car, cx, cy, across, 'drive') || game.vehicles.some((o) => Math.hypot(o.x - cx, o.y - cy) < 4)) { cars.length = 0; break; }
      car.siren = true; cars.push(car);
    }
    if (!cars.length) continue;
    game.vehicles.push(...cars);
    spawnOfficers(game, x + (axis === 'v' ? width / 2 + 1 : 0), y + (axis === 'v' ? 0 : width / 2 + 1), p.id, 2);
    game.say('ROADBLOCK AHEAD', p);
    game.events.push({ kind: 'click', x, y });
    return true;
  }
  return false;
}
